import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Upload, User, FileText, CheckCircle, Plus, Trash2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '@/context/LanguageContext';
import { trackCompleteRegistration, trackInitiateCheckout } from '@/utils/metaPixel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VisaApplication = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [nationalities, setNationalities] = useState([]);
  const [rates, setRates] = useState({});
  const [visaFee, setVisaFee] = useState(500);
  const [visaDuration, setVisaDuration] = useState('30_days');

  const [formData, setFormData] = useState({
    user_email: '',
    user_phone: '',
    nationality: '',
    first_name: '',
    last_name: '',
    father_name: '',
    passport_number: '',
    place_of_birth: ''
  });

  const [files, setFiles] = useState({
    passport_photo: null,
    personal_photo: null,
    id_front: null,
    id_back: null
  });

  const [familyMembers, setFamilyMembers] = useState([]);
  const [familyFiles, setFamilyFiles] = useState({});
  const [documentRequirements, setDocumentRequirements] = useState({
    default: [
      { id: 'passport_photo', name: 'Passport Copy', required: true },
      { id: 'personal_photo', name: 'Personal Picture', required: true },
      { id: 'id_front', name: 'ID Card Front', required: true },
      { id: 'id_back', name: 'ID Card Back', required: false }
    ],
    by_nationality: {}
  });

  useEffect(() => {
    fetchNationalities();
    fetchDocumentRequirements();
  }, []);

  useEffect(() => {
    if (formData.nationality && rates[formData.nationality]) {
      const nationalityRates = rates[formData.nationality];
      if (typeof nationalityRates === 'object') {
        setVisaFee(nationalityRates[visaDuration] || nationalityRates['30_days'] || 500);
      } else {
        setVisaFee(nationalityRates);
      }
    }
  }, [formData.nationality, rates, visaDuration, familyMembers.length]);

  const fetchNationalities = async () => {
    try {
      const response = await axios.get(`${API}/nationalities`);
      setNationalities(response.data.nationalities);
      setRates(response.data.rates);
    } catch (error) {
      console.error('Error fetching nationalities:', error);
      toast.error(t.common.error);
    }
  };

  const fetchDocumentRequirements = async () => {
    try {
      const response = await axios.get(`${API}/document-requirements`);
      setDocumentRequirements(response.data);
    } catch (error) {
      console.error('Error fetching document requirements:', error);
    }
  };

  // Get required documents for the selected nationality
  const getRequiredDocuments = () => {
    const nationality = formData.nationality;
    if (nationality && documentRequirements.by_nationality[nationality]) {
      return documentRequirements.by_nationality[nationality].filter(d => d.required);
    }
    return documentRequirements.default.filter(d => d.required);
  };

  // Get all documents (required and optional) for the selected nationality
  const getAllDocuments = () => {
    const nationality = formData.nationality;
    if (nationality && documentRequirements.by_nationality[nationality]) {
      return documentRequirements.by_nationality[nationality];
    }
    return documentRequirements.default;
  };

  const getTotalFee = () => {
    const totalApplicants = 1 + familyMembers.length;
    return visaFee * totalApplicants;
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleNationalityChange = (value) => {
    setFormData({ ...formData, nationality: value });
  };

  const handleDurationChange = (value) => {
    setVisaDuration(value);
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size should be less than 5MB');
        return;
      }
      setFiles({ ...files, [fieldName]: file });
    }
  };

  const addFamilyMember = () => {
    const newMember = {
      id: Date.now(),
      first_name: '',
      last_name: '',
      father_name: '',
      passport_number: '',
      place_of_birth: ''
    };
    setFamilyMembers([...familyMembers, newMember]);
    setFamilyFiles({ ...familyFiles, [newMember.id]: {} });
  };

  const removeFamilyMember = (id) => {
    setFamilyMembers(familyMembers.filter(m => m.id !== id));
    const newFiles = { ...familyFiles };
    delete newFiles[id];
    setFamilyFiles(newFiles);
  };

  const updateFamilyMember = (id, field, value) => {
    setFamilyMembers(familyMembers.map(m =>
      m.id === id ? { ...m, [field]: value } : m
    ));
  };

  const handleFamilyFileChange = (memberId, fieldName, e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size should be less than 5MB');
        return;
      }
      setFamilyFiles({
        ...familyFiles,
        [memberId]: {
          ...(familyFiles[memberId] || {}),
          [fieldName]: file
        }
      });
    }
  };

  const validateStep1 = () => {
    if (!formData.user_email || !formData.user_phone || !formData.nationality || 
        !formData.first_name || !formData.last_name) {
      toast.error(t.common.error);
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.father_name || !formData.passport_number || !formData.place_of_birth) {
      toast.error(t.common.error);
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    // Get required documents for this nationality
    const requiredDocs = getRequiredDocuments();
    
    for (const doc of requiredDocs) {
      if (!files[doc.id]) {
        toast.error(`Please upload: ${doc.name}`);
        return false;
      }
    }

    for (const member of familyMembers) {
      const memberFiles = familyFiles[member.id] || {};
      for (const doc of requiredDocs) {
        if (!memberFiles[doc.id]) {
          toast.error(`Please upload ${doc.name} for ${member.first_name} ${member.last_name}`);
          return false;
        }
      }
    }
    return true;
  };

  const validateStep4 = () => {
    for (const member of familyMembers) {
      if (!member.first_name || !member.last_name || !member.father_name || 
          !member.passport_number || !member.place_of_birth) {
        toast.error(t.common.error);
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;
    if (currentStep === 3 && !validateStep4()) return;
    if (currentStep === 4 && !validateStep3()) return;
    
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep1() || !validateStep2() || !validateStep3() || !validateStep4()) {
      return;
    }

    setLoading(true);
    
    try {
      const formDataToSend = new FormData();
      
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });
      
      formDataToSend.append('visa_duration', visaDuration);
      
      Object.keys(files).forEach(key => {
        if (files[key]) {
          formDataToSend.append(key, files[key]);
        }
      });

      const familyData = familyMembers.map(({ id, ...member }) => member);
      formDataToSend.append('family_data', JSON.stringify(familyData));

      familyMembers.forEach(member => {
        const memberFiles = familyFiles[member.id] || {};
        ['passport_photo', 'personal_photo', 'id_front', 'id_back'].forEach(docType => {
          if (memberFiles[docType]) {
            formDataToSend.append('family_files', memberFiles[docType]);
          }
        });
      });

      const response = await axios.post(`${API}/applications`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Track application submission with Meta Pixel
      trackCompleteRegistration({
        visaType: visaDuration === '30_days' ? '30 Days Visa' : '60 Days Visa',
        value: visaFee,
        nationality: formData.nationality
      });

      toast.success(t.common.success);
      navigate(`/payment/${response.data.id}`);
    } catch (error) {
      console.error('Application error:', error);
      
      if (error.response?.status === 422) {
        const validationErrors = error.response?.data?.detail;
        if (Array.isArray(validationErrors)) {
          const errorMessages = validationErrors.map(err => `${err.loc.join('.')}: ${err.msg}`).join(', ');
          toast.error(`${t.common.error}: ${errorMessages}`);
        } else {
          toast.error(t.common.error);
        }
      } else {
        toast.error(error.response?.data?.detail || t.common.error);
      }
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { number: 1, title: t.apply.step1, icon: <User className="w-5 h-5" /> },
    { number: 2, title: t.apply.step2, icon: <FileText className="w-5 h-5" /> },
    { number: 3, title: t.apply.step4, icon: <Users className="w-5 h-5" /> },
    { number: 4, title: t.apply.step3, icon: <Upload className="w-5 h-5" /> },
    { number: 5, title: t.apply.step5, icon: <CheckCircle className="w-5 h-5" /> }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-12" data-testid="visa-application-page">
      <div className="container mx-auto px-6 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 text-center"
        >
          <h1 className="text-4xl font-heading font-bold text-[#1A4D7F] mb-3">
            {t.apply.title}
          </h1>
          <p className="text-slate-600">{t.apply.subtitle}</p>
        </motion.div>

        {/* Progress Steps */}
        <div className="mb-12 overflow-x-auto">
          <div className="flex justify-between items-center min-w-[600px]">
            {steps.map((step, index) => (
              <React.Fragment key={step.number}>
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold transition-all ${
                      currentStep >= step.number
                        ? 'bg-[#1A4D7F] text-white shadow-lg'
                        : 'bg-slate-200 text-slate-500'
                    }`}
                    data-testid={`step-indicator-${step.number}`}
                  >
                    {step.icon}
                  </div>
                  <p className={`text-sm mt-2 font-medium ${currentStep >= step.number ? 'text-[#1A4D7F]' : 'text-slate-500'}`}>
                    {step.title}
                  </p>
                </div>
                {index < steps.length - 1 && (
                  <div className={`h-1 flex-1 mx-2 rounded ${currentStep > step.number ? 'bg-[#1A4D7F]' : 'bg-slate-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <Card className="border-slate-200 shadow-lg">
          <CardHeader className="bg-white border-b border-slate-100">
            <CardTitle className="text-2xl font-heading text-[#1A4D7F]">
              {steps[currentStep - 1].title}
            </CardTitle>
            <CardDescription>{t.apply.subtitle}</CardDescription>
          </CardHeader>

          <CardContent className="p-6 md:p-8">
            {/* Step 1: Personal Info */}
            {currentStep === 1 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
                data-testid="step-1-personal-info"
              >
                <div>
                  <Label htmlFor="nationality">{t.apply.nationality} *</Label>
                  <Select value={formData.nationality} onValueChange={handleNationalityChange}>
                    <SelectTrigger className="mt-2" data-testid="nationality-select">
                      <SelectValue placeholder={t.apply.selectNationality} />
                    </SelectTrigger>
                    <SelectContent>
                      {nationalities.map(nat => (
                        <SelectItem key={nat} value={nat}>
                          {nat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="visa_duration">{t.apply.visaDuration} *</Label>
                  <Select value={visaDuration} onValueChange={handleDurationChange}>
                    <SelectTrigger className="mt-2" data-testid="duration-select">
                      <SelectValue placeholder={t.apply.selectDuration} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30_days">{t.apply.days30}</SelectItem>
                      <SelectItem value="60_days">{t.apply.days60}</SelectItem>
                    </SelectContent>
                  </Select>
                  {formData.nationality && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-700 font-medium">
                        {t.apply.visaFee}: {visaFee} AED {t.apply.perPerson} ({visaDuration === '30_days' ? '30' : '60'} {t.common.days})
                      </p>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="first_name">{t.apply.firstName} *</Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      placeholder={t.apply.firstName}
                      className="mt-2"
                      data-testid="first-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">{t.apply.lastName} *</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      placeholder={t.apply.lastName}
                      className="mt-2"
                      data-testid="last-name-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="user_email">{t.apply.email} *</Label>
                  <Input
                    id="user_email"
                    name="user_email"
                    type="email"
                    value={formData.user_email}
                    onChange={handleInputChange}
                    placeholder={t.apply.emailPlaceholder}
                    className="mt-2"
                    data-testid="email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="user_phone">{t.apply.phone} *</Label>
                  <Input
                    id="user_phone"
                    name="user_phone"
                    type="tel"
                    value={formData.user_phone}
                    onChange={handleInputChange}
                    placeholder={t.apply.phonePlaceholder}
                    className="mt-2"
                    data-testid="phone-input"
                  />
                </div>
              </motion.div>
            )}

            {/* Step 2: Passport Details */}
            {currentStep === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
                data-testid="step-2-passport-details"
              >
                <div>
                  <Label htmlFor="father_name">{t.apply.fatherName} *</Label>
                  <Input
                    id="father_name"
                    name="father_name"
                    value={formData.father_name}
                    onChange={handleInputChange}
                    placeholder={t.apply.fatherName}
                    className="mt-2"
                    data-testid="father-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="passport_number">{t.apply.passportNumber} *</Label>
                  <Input
                    id="passport_number"
                    name="passport_number"
                    value={formData.passport_number}
                    onChange={handleInputChange}
                    placeholder={t.apply.passportNumber}
                    className="mt-2"
                    data-testid="passport-number-input"
                  />
                </div>

                <div>
                  <Label htmlFor="place_of_birth">{t.apply.placeOfBirth} *</Label>
                  <Input
                    id="place_of_birth"
                    name="place_of_birth"
                    value={formData.place_of_birth}
                    onChange={handleInputChange}
                    placeholder={t.apply.placeOfBirth}
                    className="mt-2"
                    data-testid="place-of-birth-input"
                  />
                </div>
              </motion.div>
            )}

            {/* Step 3: Family Members */}
            {currentStep === 3 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
                data-testid="step-3-family-members"
              >
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-900">
                    <strong>{t.apply.familyMembers}:</strong> {t.apply.familyMemberDetails}
                  </p>
                </div>

                <Button
                  type="button"
                  onClick={addFamilyMember}
                  variant="outline"
                  className="w-full border-[#1A4D7F] text-[#1A4D7F] hover:bg-blue-50"
                  data-testid="add-family-member-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t.apply.addFamilyMember}
                </Button>

                {familyMembers.map((member, index) => (
                  <Card key={member.id} className="border-slate-300">
                    <CardHeader className="pb-3">
                      <div className="flex justify-between items-center">
                        <CardTitle className="text-lg">{t.apply.familyMembers} {index + 1}</CardTitle>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFamilyMember(member.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>{t.apply.firstName} *</Label>
                          <Input
                            value={member.first_name}
                            onChange={(e) => updateFamilyMember(member.id, 'first_name', e.target.value)}
                            placeholder={t.apply.firstName}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>{t.apply.lastName} *</Label>
                          <Input
                            value={member.last_name}
                            onChange={(e) => updateFamilyMember(member.id, 'last_name', e.target.value)}
                            placeholder={t.apply.lastName}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>{t.apply.fatherName} *</Label>
                          <Input
                            value={member.father_name}
                            onChange={(e) => updateFamilyMember(member.id, 'father_name', e.target.value)}
                            placeholder={t.apply.fatherName}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>{t.apply.passportNumber} *</Label>
                          <Input
                            value={member.passport_number}
                            onChange={(e) => updateFamilyMember(member.id, 'passport_number', e.target.value)}
                            placeholder={t.apply.passportNumber}
                            className="mt-1"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>{t.apply.placeOfBirth} *</Label>
                        <Input
                          value={member.place_of_birth}
                          onChange={(e) => updateFamilyMember(member.id, 'place_of_birth', e.target.value)}
                          placeholder={t.apply.placeOfBirth}
                          className="mt-1"
                        />
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {familyMembers.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>{t.apply.familyMemberDetails}</p>
                  </div>
                )}
              </motion.div>
            )}

            {/* Step 4: Documents */}
            {currentStep === 4 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
                data-testid="step-4-documents"
              >
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <h4 className="font-semibold text-amber-900 mb-2">{t.apply.uploadDocuments}</h4>
                  <p className="text-sm text-amber-800">{formData.first_name} {formData.last_name}</p>
                  {formData.nationality && (
                    <p className="text-xs text-amber-700 mt-1">
                      Required documents for {formData.nationality} nationals
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {getAllDocuments().map((doc) => (
                    <div key={doc.id} className={`space-y-2 ${!doc.required ? 'opacity-70' : ''}`}>
                      <Label htmlFor={doc.id}>
                        {doc.name} {doc.required ? '*' : '(Optional)'}
                      </Label>
                      <Input
                        id={doc.id}
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => handleFileChange(e, doc.id)}
                        className="mt-2"
                        data-testid={`${doc.id}-upload`}
                      />
                      {files[doc.id] && (
                        <p className="text-sm text-green-600 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" />
                          {files[doc.id].name}
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                {/* Family member documents */}
                {familyMembers.map((member, index) => (
                  <div key={member.id} className="mt-8 pt-6 border-t border-slate-200">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <h4 className="font-semibold text-blue-900 mb-1">
                        {t.apply.familyMembers} {index + 1} - {t.apply.uploadDocuments}
                      </h4>
                      <p className="text-sm text-blue-800">
                        {member.first_name} {member.last_name}
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {getAllDocuments().map((doc) => (
                        <div key={doc.id} className={`space-y-2 ${!doc.required ? 'opacity-70' : ''}`}>
                          <Label>{doc.name} {doc.required ? '*' : '(Optional)'}</Label>
                          <Input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={(e) => handleFamilyFileChange(member.id, doc.id, e)}
                            className="mt-2"
                          />
                          {familyFiles[member.id]?.[doc.id] && (
                            <p className="text-sm text-green-600 flex items-center gap-1">
                              <CheckCircle className="w-4 h-4" />
                              {familyFiles[member.id][doc.id].name}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </motion.div>
            )}

            {/* Step 5: Review */}
            {currentStep === 5 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
                data-testid="step-5-review"
              >
                <div className="bg-slate-50 rounded-lg p-6 space-y-4">
                  <h3 className="font-heading font-semibold text-lg text-[#1A4D7F] mb-4">
                    {t.apply.applicantInfo}
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">{t.apply.nationality}</p>
                      <p className="font-medium">{formData.nationality}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">{t.apply.firstName} {t.apply.lastName}</p>
                      <p className="font-medium">{formData.first_name} {formData.last_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">{t.apply.fatherName}</p>
                      <p className="font-medium">{formData.father_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">{t.apply.email}</p>
                      <p className="font-medium">{formData.user_email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">{t.apply.phone}</p>
                      <p className="font-medium">{formData.user_phone}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">{t.apply.passportNumber}</p>
                      <p className="font-medium">{formData.passport_number}</p>
                    </div>
                  </div>
                </div>

                {familyMembers.length > 0 && (
                  <div className="bg-blue-50 rounded-lg p-6">
                    <h3 className="font-heading font-semibold text-lg text-[#1A4D7F] mb-4">
                      {t.apply.familyMembers} ({familyMembers.length})
                    </h3>
                    {familyMembers.map((member, index) => (
                      <div key={member.id} className="mb-3 pb-3 border-b border-blue-200 last:border-0">
                        <p className="font-medium">
                          {index + 1}. {member.first_name} {member.last_name}
                        </p>
                        <p className="text-sm text-slate-600">{t.apply.passportNumber}: {member.passport_number}</p>
                      </div>
                    ))}
                  </div>
                )}

                <div className="bg-gradient-to-r from-[#1A4D7F] to-[#009FB7] rounded-lg p-6 text-white">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-lg">{t.apply.visaFee} {t.apply.perPerson}:</span>
                    <span className="text-2xl font-heading font-bold">{visaFee} AED</span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-lg">{t.apply.totalApplicants}:</span>
                    <span className="text-2xl font-heading font-bold">{1 + familyMembers.length}</span>
                  </div>
                  <div className="border-t border-white/30 mt-3 pt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-semibold">{t.apply.totalFee}:</span>
                      <span className="text-3xl font-heading font-bold">{getTotalFee()} AED</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-slate-200">
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={currentStep === 1 || loading}
                data-testid="back-button"
              >
                {t.apply.previous}
              </Button>

              {currentStep < 5 ? (
                <Button
                  onClick={handleNext}
                  className="bg-[#1A4D7F] hover:bg-[#153e66]"
                  data-testid="next-button"
                >
                  {t.apply.next}
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="bg-[#009FB7] hover:bg-[#008fa5]"
                  data-testid="submit-application-button"
                >
                  {loading ? t.apply.processing : t.apply.submitApplication}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VisaApplication;
